import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  ArrowLeft, Building, MapPin, Shield, BarChart3, User, CheckCircle, XCircle, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../utils/config';

export default function PropertyConfigPage() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusLoading, setStatusLoading] = useState(false);

  useEffect(() => { fetchProperty(); }, [propertyId]);

  const fetchProperty = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('hw_token');
      const res = await axios.get(`${API}/api/properties/${propertyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperty(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Errore nel caricamento immobile');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setStatusLoading(true);
      const token = localStorage.getItem('hw_token');
      const res = await axios.patch(`${API}/api/properties/${propertyId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      setProperty(prev => ({ ...prev, status: newStatus }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel cambio stato');
    } finally {
      setStatusLoading(false);
    }
  };

  const statusColors = {
    active: 'bg-green-50 text-green-700 border-green-200',
    private: 'bg-amber-50 text-amber-700 border-amber-200',
    terminated: 'bg-red-50 text-red-700 border-red-200',
  };
  const statusLabels = { active: 'Attivo', private: 'Private', terminated: 'Cessato' };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </Layout>
    );
  }

  if (!property) {
    return (
      <Layout>
        <div className="text-center py-20 text-gray-500">
          <Building className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p className="text-lg font-medium">Immobile non trovato</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Torna indietro
          </Button>
        </div>
      </Layout>
    );
  }

  const hasQuestura = property.alloggiati_enabled && property.alloggiati_wskey;
  const hasIstat = !!(property.istat_comune_code || property.tourism_observatory);
  const status = property.status || 'active';

  return (
    <Layout>
      <div className="space-y-4 max-w-4xl mx-auto" data-testid="property-config-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold flex items-center gap-2" translate="no">
              <Building className="h-5 w-5 text-blue-600" />
              {property.name}
            </h1>
            <p className="text-sm text-gray-500 flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" />
              {[property.city, property.province, property.region].filter(Boolean).join(', ')}
            </p>
          </div>
          <Select value={status} onValueChange={handleStatusChange} disabled={statusLoading}>
            <SelectTrigger className={`w-[130px] h-9 text-sm font-medium border ${statusColors[status] || ''}`} data-testid="property-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Attivo</SelectItem>
              <SelectItem value="private">Private</SelectItem>
              <SelectItem value="terminated">Cessato</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Info generali */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Building className="h-4 w-4" /> Informazioni Generali
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <InfoRow label="Codice" value={property.property_code} />
              <InfoRow label="Indirizzo" value={property.address} />
              <InfoRow label="Tipo" value={property.property_type} />
              <InfoRow label="CIR" value={property.cir} />
              <InfoRow label="CIN" value={property.cin} />
              <InfoRow label="Max Ospiti" value={property.max_guests} />
              <InfoRow label="Camere" value={property.bedrooms} />
              <InfoRow label="Bagni" value={property.bathrooms} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <User className="h-4 w-4" /> Proprietario e Responsabile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <InfoRow label="Proprietario" value={property.owner_name} />
              <InfoRow label="Email Proprietario" value={property.owner_email} />
              <InfoRow label="Responsabile" value={property.responsible_email} />
              <InfoRow label="Cod. Partner" value={property.partner_code} />
            </CardContent>
          </Card>
        </div>

        {/* Questura e ISTAT */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className={hasQuestura ? 'border-green-200' : 'border-red-200'}>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Shield className="h-4 w-4" /> Questura (AlloggiatiWeb)
                {hasQuestura
                  ? <Badge className="bg-green-100 text-green-700 ml-auto"><CheckCircle className="h-3 w-3 mr-1" /> Configurata</Badge>
                  : <Badge className="bg-red-100 text-red-700 ml-auto"><XCircle className="h-3 w-3 mr-1" /> Non configurata</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <InfoRow label="Abilitata" value={property.alloggiati_enabled ? 'Si' : 'No'} />
              <InfoRow label="WS Key" value={property.alloggiati_wskey ? '****' + property.alloggiati_wskey.slice(-4) : '-'} />
              <InfoRow label="Cod. Struttura" value={property.alloggiati_structure_code} />
              <InfoRow label="Tipo Struttura" value={property.alloggiati_structure_type} />
              <InfoRow label="ID Appartamento" value={property.alloggiati_apartment_id} />
              <InfoRow label="Cod. Questura" value={property.police_station_code} />
            </CardContent>
          </Card>

          <Card className={hasIstat ? 'border-green-200' : 'border-red-200'}>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <BarChart3 className="h-4 w-4" /> ISTAT
                {hasIstat
                  ? <Badge className="bg-green-100 text-green-700 ml-auto"><CheckCircle className="h-3 w-3 mr-1" /> Configurato</Badge>
                  : <Badge className="bg-red-100 text-red-700 ml-auto"><XCircle className="h-3 w-3 mr-1" /> Non configurato</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <InfoRow label="Cod. Comune ISTAT" value={property.istat_comune_code} />
              <InfoRow label="Osservatorio Turistico" value={property.tourism_observatory} />
            </CardContent>
          </Card>
        </div>

        {/* Tariffe */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Tariffe</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <InfoRow label="Prezzo/notte" value={property.price_per_night != null ? `${property.price_per_night} EUR` : '-'} />
              <InfoRow label="Pulizia" value={property.cleaning_cost != null ? `${property.cleaning_cost} EUR` : '-'} />
              <InfoRow label="Biancheria/persona" value={property.linen_cost_per_person != null ? `${property.linen_cost_per_person} EUR` : '-'} />
              <InfoRow label="Last Minute" value={property.last_minute_enabled ? 'Abilitato' : 'Disabilitato'} />
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between items-center py-1 border-b border-gray-50 last:border-0">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-right max-w-[60%] truncate" translate="no">{value || '-'}</span>
    </div>
  );
}
